/**
 * Prospect Service
 * 
 * This service provides functions for creating and managing prospect entries.
 */

// API endpoint for the createProspect lambda function
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://api.eaglesrefugeacademy.com';

/**
 * Create a new prospect
 * @param {Object} prospectData - Prospect data including name, email, phone, etc.
 * @returns {Promise<Object>} - Created prospect object
 */
export const createProspect = async (prospectData) => {
  try {
    const response = await fetch(`${API_ENDPOINT}/prospects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ prospectData })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create prospect');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating prospect:', error);
    throw error;
  }
};

/**
 * Get all prospects
 * @returns {Promise<Array>} - Array of prospect objects
 */
export const getProspects = async () => {
  try {
    const response = await fetch(`${API_ENDPOINT}/prospects`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get prospects');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting prospects:', error);
    throw error;
  }
};

/**
 * Get prospect by ID
 * @param {number} prospectId - Prospect ID
 * @returns {Promise<Object>} - Prospect object
 */
export const getProspectById = async (prospectId) => {
  try {
    const response = await fetch(`${API_ENDPOINT}/prospects/${prospectId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to get prospect with ID ${prospectId}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error getting prospect with ID ${prospectId}:`, error);
    throw error;
  }
};

/**
 * Update prospect
 * @param {number} prospectId - Prospect ID
 * @param {Object} prospectData - Prospect data to update
 * @returns {Promise<Object>} - Updated prospect object
 */
export const updateProspect = async (prospectId, prospectData) => {
  try {
    const response = await fetch(`${API_ENDPOINT}/prospects/${prospectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ prospectData })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to update prospect with ID ${prospectId}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error updating prospect with ID ${prospectId}:`, error);
    throw error;
  }
};

/**
 * Delete prospect
 * @param {number} prospectId - Prospect ID
 * @returns {Promise<boolean>} - Success status
 */
export const deleteProspect = async (prospectId) => {
  try {
    const response = await fetch(`${API_ENDPOINT}/prospects/${prospectId}`, {
      method: 'DELETE'
    });

    if (!response.ok && response.status !== 204) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to delete prospect with ID ${prospectId}`);
    }

    return true;
  } catch (error) {
    console.error(`Error deleting prospect with ID ${prospectId}:`, error);
    throw error;
  }
};
